import React, { useEffect, useState } from 'react';
import IndividualPricingComparison from './IndividualPricingComparison';
import PricingScheduleActions from './PricingScheduleActions';
import { Link } from 'react-router-dom';
import { formatSouthAfricanDateTime } from '../../../utils/southAfricanTime';
import { pricingVersionLabel } from '../../../utils/pricingVersionLabel';
import IndividualPricingEditor, { pricingFieldLabel, PricingLeaveGuard } from './IndividualPricingEditor';
import { getPricingReadiness, listPricingDrafts, pricingLoadError, pricingActivationError, publishPricingDraft,
  PricingDraftPage, PricingDraftSummary, PublicationReadiness, listPricingPublications, PricingPublication } from '../../../services/individualPricingSettings';

const publicationStatus = {
  ACTIVE: { label: 'Active', badge: 'bg-success', order: 0 },
  SCHEDULED: { label: 'Scheduled', badge: 'bg-warning text-dark', order: 1 },
  REPLACED: { label: 'Replaced', badge: 'bg-secondary', order: 2 },
  CANCELLED: { label: 'Cancelled', badge: 'bg-danger', order: 3 },
};

function PricingSettingsPanel({ embedded, children }: { embedded: boolean; children: React.ReactNode }) {
  if (embedded) return <>{children}</>;
  return <div>
    <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
      <div className="container-fluid">
        <div className="row g-3 mb-3 align-items-center">
          <div className="col">
            <ol className="breadcrumb bg-transparent mb-0">
              <li className="breadcrumb-item"><Link className="text-white" to="/">
                <i className="fa fa-home me-2" aria-hidden="true"></i>Home
              </Link></li>
              <li className="breadcrumb-item"><Link className="text-white" to="/settings">Settings</Link></li>
              <li className="breadcrumb-item active text-white" aria-current="page">Loan Pricing Settings</li>
            </ol>
          </div>
        </div>
        <div className="row align-items-center">
          <div className="col-md-8">
            <h1 className="fs-2 fw-bold mb-2 text-white">Loan Pricing Settings</h1>
            <p className="mb-0 text-white fs-6 opacity-75">Manage individual loan pricing drafts and review revisions before activation.</p>
          </div>
        </div>
      </div>
    </div>
    <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
      <div className="container-fluid">
        <div className="row"><div className="col-12">
          <div className="card"><div className="card-body">{children}</div></div>
        </div></div>
      </div>
    </div>
  </div>;
}

export default function IndividualPricingSettings({ embedded = false, onLeaveGuardChange }: {
  embedded?: boolean; onLeaveGuardChange?: (guard: PricingLeaveGuard) => void;
}) {
  const [publications, setPublications] = useState<PricingPublication[]>([]);
  const [publicationError, setPublicationError] = useState('');
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [scheduleMessage, setScheduleMessage] = useState('');
  const [cursor, setCursor] = useState<string>();
  const [reload, setReload] = useState(0);
  const [page, setPage] = useState<PricingDraftPage>();
  const [selected, setSelected] = useState<PricingDraftSummary>();
  const [readiness, setReadiness] = useState<PublicationReadiness>();
  const [error, setError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [activationError, setActivationError] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailReload, setDetailReload] = useState(0);
  const [editor, setEditor] = useState<{ id?: string } | null>(null);
  const [comparison, setComparison] = useState<PricingDraftSummary>();
  const [publishing, setPublishing] = useState(false);
  const [publishReason, setPublishReason] = useState('');
  const [versionName, setVersionName] = useState('');
  const [activation, setActivation] = useState<{ versionId: string; displayName?: string; effectiveFrom: string }>();
  const drafts = page?.items ?? [];
  const blockers = readiness?.blockers ?? [];

  useEffect(() => {
    let current = true;
    listPricingPublications().then(rows => { if (current) { setPublications(rows || []); setPublicationError(''); } })
      .catch(() => { if (current) setPublicationError('Published pricing status could not be loaded. Reload to check activation.'); });
    return () => { current = false; };
  }, [reload]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true); setError(''); setPage(undefined); setSelected(undefined); setReadiness(undefined);
    listPricingDrafts(cursor, controller.signal).then(result => {
      if (active) setPage(result);
    }).catch(e => { if (active) setError(pricingLoadError(e)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [cursor, reload]);

  useEffect(() => {
    setReadiness(undefined); setDetailError(''); setActivationError('');
    if (!selected) { setDetailLoading(false); return; }
    const controller = new AbortController();
    let active = true;
    setDetailLoading(true);
    getPricingReadiness(selected.policyId, controller.signal).then(result => {
      if (!active) return;
      if (result.policyId !== selected.policyId || result.revision !== selected.revision) {
        setDetailError('This draft changed since the list was loaded. Reload the list to view the latest revision.');
      } else setReadiness(result);
    }).catch(e => { if (active) setDetailError(pricingLoadError(e)); })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [selected, detailReload]);

  if (comparison) return <PricingSettingsPanel embedded={embedded}><IndividualPricingComparison key={comparison.policyId} id={comparison.policyId}
    revision={comparison.revision} onClose={() => setComparison(undefined)} /></PricingSettingsPanel>;
  if (editor) return <PricingSettingsPanel embedded={embedded}><IndividualPricingEditor id={editor.id} onLeaveGuardChange={onLeaveGuardChange} onClose={() => {
    setEditor(null); setCursor(undefined); setReload(n => n + 1);
  }} /></PricingSettingsPanel>;
  return <PricingSettingsPanel embedded={embedded}><section>
    <h2 className="h5 mb-3">Individual Loan Pricing</h2>
    <p className="text-muted mb-4">Saved pricing drafts are not active loan prices. Viewing a draft does not change applications or agreements.</p>
    <button className="btn btn-primary btn-sm mb-3 me-2" disabled={scheduleBusy} onClick={() => setEditor({})}>New pricing draft</button>
    <button className="btn btn-outline-primary btn-sm mb-3" disabled={loading || scheduleBusy}
      onClick={() => { setCursor(undefined); setReload(n => n + 1); }}>Reload list</button>
    {publicationError && <p role="alert">{publicationError}</p>}
    {scheduleMessage && <p role="status" className="alert alert-success">{scheduleMessage}</p>}
    {publications.length > 0 && <section aria-label="Published pricing" className="mb-4">
      <h3 className="h6 mb-3">Published pricing</h3>
      <div className="table-responsive border rounded">
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light"><tr>
            <th scope="col" className="px-3 py-3">Pricing name</th>
            <th scope="col" className="py-3">Revision</th>
            <th scope="col" className="py-3">Status</th>
            <th scope="col" className="py-3">Effective from</th>
            <th scope="col" className="pe-3 py-3">Actions</th>
          </tr></thead>
          <tbody>{[...publications].sort((first, second) => publicationStatus[first.status].order - publicationStatus[second.status].order)
            .map(version => <tr key={version.versionId}>
              <td className="px-3 py-3">
                <div className="fw-semibold">{pricingVersionLabel(version)}</div>
                <details className="small text-muted mt-1"><summary>Technical details</summary>
                  <div className="text-break">Version ID: {version.versionId}
                    {version.rescheduledFrom && <><br />Rescheduled from: {version.rescheduledFrom}</>}</div>
                </details>
              </td>
              <td>{version.revision}</td>
              <td><span className={`badge ${publicationStatus[version.status].badge}`}>{publicationStatus[version.status].label}</span></td>
              <td className="text-nowrap">
                <div>{formatSouthAfricanDateTime(version.effectiveFrom)}</div>
                {version.cancelledAt && <div className="small text-muted mt-1">Cancelled {formatSouthAfricanDateTime(version.cancelledAt)}</div>}
              </td>
              <td className="pe-3">{version.status === 'SCHEDULED' ? <PricingScheduleActions version={version}
                disabled={scheduleBusy || publishing} onBusy={setScheduleBusy} onChanged={message => {
                  setScheduleMessage(message); setActivation(undefined); setCursor(undefined); setReload(n => n + 1);
                }} /> : <span className="text-muted">—</span>}</td>
            </tr>)}</tbody>
        </table>
      </div>
    </section>}
    {activation && <div role="status" className="alert alert-success">Pricing revision published: {pricingVersionLabel(activation)}; effective {formatSouthAfricanDateTime(activation.effectiveFrom)}. New quotations use it from that time.
      <details><summary>Technical details</summary>Version ID: {activation.versionId}</details></div>}
    {loading && <p role="status">Loading pricing drafts…</p>}
    {error && <p role="alert">{error}</p>}
    {page && <>
      {drafts.length === 0 ? <p>No saved pricing drafts found.</p> : <div className="table-responsive">
        <table className="table align-middle">
          <thead><tr><th>Pricing name</th><th>Revision</th><th>Last saved</th><th>Action</th></tr></thead>
          <tbody>{drafts.map(draft => <tr key={draft.policyId}>
            <td><span title={draft.policyId}>{draft.displayName || `Individual pricing · ${draft.policyId.slice(0,8)}`}</span></td><td>{draft.revision}</td><td>{formatSouthAfricanDateTime(draft.changedAt)}</td>
            <td><button className="btn btn-outline-primary btn-sm" onClick={() => { setVersionName(draft.displayName || ''); setSelected(draft); }}
              aria-label={`View draft ${draft.policyId}`}>View draft</button>
              <button className="btn btn-outline-primary btn-sm ms-2" disabled={scheduleBusy} onClick={() => setEditor({ id: draft.policyId })}
                aria-label={`Edit draft ${draft.policyId}`}>Edit</button></td>
          </tr>)}</tbody>
        </table>
      </div>}
      {page.nextCursor && <button className="btn btn-outline-secondary btn-sm"
        onClick={() => setCursor(page.nextCursor || undefined)}>Next page</button>}
    </>}
    {selected && <section className="border-top mt-4 pt-3" aria-label="Selected draft">
      <h2 className="h5">{selected.displayName ? `${selected.displayName} · revision` : 'Draft revision'} {selected.revision}</h2>
      <p className="text-break">{selected.policyId}</p>
      {detailLoading && <p role="status">Checking publication requirements…</p>}
      {detailError && <><p role="alert">{detailError}</p><button className="btn btn-outline-secondary btn-sm"
        onClick={() => setDetailReload(n => n + 1)}>Retry requirements</button></>}
      {activationError && <><p role="alert">{activationError}</p><button className="btn btn-outline-secondary btn-sm"
        disabled={publishing} onClick={() => setDetailReload(n => n + 1)}>Recheck requirements</button></>}
      {readiness && <>
        <button className="btn btn-outline-secondary btn-sm mb-2" onClick={() => setComparison(selected)}>Preview repayments</button>
        <p className="text-muted">Preview the repayment schedule, then approve and activate this exact revision.</p>
        {blockers.length > 0 ? <ul>{blockers.map((blocker, index) =>
          <li key={`${blocker.code}-${blocker.field}-${index}`}>{pricingFieldLabel(blocker.field)}: {blocker.code === 'POLICY_MISSING' ? 'Not yet configured.' : blocker.message}</li>)}</ul>
          : <>
            <label className="form-label" htmlFor="pricing-version-name">Pricing version name (optional)</label>
            <input id="pricing-version-name" className="form-control" maxLength={80} value={versionName}
              placeholder="Generated at activation if left blank"
              onChange={event => setVersionName(event.target.value)} />
            <p className="form-text">Use a unique name, such as October 2026 standard pricing. Names are unique across published pricing, ignoring letter case.
              Leave blank to generate a name from the effective South African time. Published names are retained with their version.</p>
            <button type="button" className="btn btn-outline-secondary btn-sm mb-2" disabled={publishing}
              onClick={() => setVersionName('')}>Generate name automatically</button>
            <label className="form-label" htmlFor="pricing-publish-reason">Approval reason</label>
            <input id="pricing-publish-reason" className="form-control mb-2" value={publishReason}
              onChange={event => setPublishReason(event.target.value)} />
            <button className="btn btn-primary" disabled={publishing || scheduleBusy || !publishReason.trim()} onClick={async () => {
              setPublishing(true); setActivationError('');
              try {
                const published = await publishPricingDraft(selected.policyId, selected.revision, publishReason.trim(), versionName.trim() || (selected.displayName ? '' : undefined));
                setActivation({versionId: published.versionId, displayName: published.displayName, effectiveFrom: published.policy.effectiveFrom!}); setPublishReason(''); setReload(value => value + 1);
              } catch (e) { setActivationError(pricingActivationError(e)); }
              finally { setPublishing(false); }
            }}>{publishing ? 'Activating…' : 'Approve and activate revision'}</button>
          </>}
      </>}
    </section>}
  </section></PricingSettingsPanel>;
}
